// Link-source entry builder for authored initial `.luis` stylesheets.
// Runtime fetches only the entry source itself; it does not resolve `.luis` imports.
import type { CachedSourceEntry, BuildSourceEntry } from './types'
import type { LuisManifestResolution } from './manifest'
import { buildLinkSignature, buildOptionsSignature } from './signature'
import { assertSecurityDependency, isAllowedLuisSourceUrl, rethrowSecurityError, verifyLuisSourceIntegrity } from '@lentystyle/guard/runtime'
import { buildRuntimeSecurityViolation, emitRuntimeSecurityViolation } from '../../security-events'

// Build one external link source entry with optional cached source reuse.
export async function buildLinkSourceEntry(
  source: HTMLLinkElement,
  styleTag: HTMLStyleElement,
  sourceId: string,
  href: string,
  previous: CachedSourceEntry | undefined,
  manifestResolution: LuisManifestResolution,
): Promise<BuildSourceEntry> {
  const optionsSignature = buildOptionsSignature(source)
  if (isUnsafeExternalLuisHref(href)) {
    return buildLinkErrorEntry(source, styleTag, sourceId, href, optionsSignature, manifestResolution, new Error('Unsafe stylesheet URL blocked.'))
  }

  try {
    if (previous && previous._kind === 'link' && previous._href === href) {
      // Re-check cached source text because runtime security policy may have changed.
      await assertLuisSourceIntegrity(href, previous._rawSource)
      return buildLinkRuntimeEntry(source, styleTag, sourceId, href, previous._rawSource, optionsSignature, manifestResolution)
    } else {
      // Continue network fetch for new or changed link sources.
    }

    const response = await fetch(href)
    if (!response.ok) {
      throw new Error(`Failed to load .luis (${response.status})`)
    }

    const rawSource = await response.text()
    await assertLuisSourceIntegrity(href, rawSource)
    return buildLinkRuntimeEntry(source, styleTag, sourceId, href, rawSource, optionsSignature, manifestResolution)
  } catch (error) {
    return buildLinkErrorEntry(source, styleTag, sourceId, href, optionsSignature, manifestResolution, error)
  }
}

// Build one runtime entry without runtime `.luis` import resolution.
function buildLinkRuntimeEntry(
  source: HTMLLinkElement,
  styleTag: HTMLStyleElement,
  sourceId: string,
  href: string,
  rawSource: string,
  optionsSignature: string,
  manifestResolution: LuisManifestResolution,
): BuildSourceEntry {
  return {
    source,
    styleTag,
    sourceId,
    kind: 'link',
    href,
    rawSource,
    debugSource: rawSource,
    signature: buildLinkSignature(href, rawSource, '', optionsSignature),
    executionOverride: manifestResolution.executionOverrideResolver?.(href),
    serializedImportGraph: {},
    serializedImportModes: {},
    importedLuisHrefs: [],
    importMetrics: [],
    preCompileWarnings: [...manifestResolution.warnings],
  }
}

// Build one fetch-error entry for external link sources.
function buildLinkErrorEntry(
  source: HTMLLinkElement,
  styleTag: HTMLStyleElement,
  sourceId: string,
  href: string,
  optionsSignature: string,
  manifestResolution: LuisManifestResolution,
  fetchError: unknown,
): BuildSourceEntry {
  return {
    source,
    styleTag,
    sourceId,
    kind: 'link',
    href,
    rawSource: '',
    debugSource: '',
    signature: `link:error:${href}\noptions:${optionsSignature}`,
    serializedImportGraph: {},
    serializedImportModes: {},
    importedLuisHrefs: [],
    importMetrics: [],
    preCompileWarnings: [...manifestResolution.warnings],
    fetchError,
  }
}

// Block executable URL schemes and disallowed cross-origin source URLs.
function isUnsafeExternalLuisHref(href: string): boolean {
  assertSecurityDependency(typeof document !== 'undefined' && Boolean(document.baseURI), 'document.baseURI')
  try {
    const blocked = !isAllowedLuisSourceUrl(href, document.baseURI)
    if (blocked) {
      emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(
        'cross-origin-source-rejected',
        'Runtime source URL was rejected by security policy.',
        { href },
      ))
    }
    return blocked
  } catch (error) {
    rethrowSecurityError(error)
    return true
  }
}

// Enforce source integrity for cross-origin `.luis` fetches.
async function assertLuisSourceIntegrity(href: string, rawSource: string): Promise<void> {
  const verified = await verifyLuisSourceIntegrity(href, document.baseURI, rawSource)
  // Stop source compilation when cross-origin integrity is missing or mismatched.
  if (!verified) {
    emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(
      'integrity-mismatch',
      'Runtime source integrity check failed.',
      { href },
    ))
    throw new Error('Unsafe stylesheet URL blocked: missing or invalid .luis source integrity.')
  } else {
    // Continue with verified source text.
  }
}
